/**
 * SetupWizard — the first-run `/setup` experience for a freshly-approved
 * self-service customer (blueprint:
 * .agents/Tasks/tcm-migration/self-serve-onboarding-wizard-blueprint.md §3-P2).
 *
 * A focused, standalone page (no sidebar/breadcrumb chrome) that walks the
 * customer through connecting their tenant: the same Connect → Verify → Size
 * → Choose scope → Watch machinery as Baseline Compliance → Monitoring
 * (shared SetupJourney component), under a brief and with the relay
 * affordance switched on ("I'm not the Microsoft 365 admin").
 *
 * Lifecycle: App.tsx routes eligible users here after sign-in
 * (needsSelfServeSetup), and Layout shows the "finish setup" banner if they
 * navigate away — soft gating (blueprint D2), the wizard never traps. Once
 * the server-side finish promotes the tenant, eligibility flips false and
 * this page renders the all-set brief; the standing health surface from then
 * on is Baseline Compliance → Monitoring.
 *
 * Carbon (blueprint §5.2 `public`, mockup carbon/01 brief, 02 tables): the
 * brand bar, then the 01 brief (BriefHero: an action-first h1, one lede, the
 * one primary action, the provenance line), then the journey section as on
 * Baseline Compliance → Monitoring. Every state keeps exactly one h1: loading
 * renders the brief's announced empty heading, and a failed load is a
 * blocking-error InlineNotification under its own brief. Conceptual copy
 * only — no internal pipeline names.
 */

import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { api, tcmApi } from '@/lib/api'
import type { ActionBrief } from '@/lib/brief-shape'
import { composeMonitoringCoverage } from '@/lib/monitoring-coverage'
import {
  coverageRefetchInterval,
  COVERAGE_POLL_MAX_FAILURES,
} from '@/lib/monitoring-actions'
import { pickSetupTenant, tenantsCacheNeedsRefresh } from '@/lib/onboarding-setup'
import type { Tenant } from '@/types'
import type { TcmCoverageSummary } from '@/types/tcm'
import SetupJourney from '@/components/Onboarding/SetupJourney'
import { SectionHeadline } from '@/components/PostureBrief/SectionHeadline'
import { BriefHero, Button, InlineNotification, Spinner } from '@/components/carbon'
import { BrandBar, PUBLIC_LINK } from '@/components/PublicLayout'

// ── The brief per state (action first; one lede with one bold run) ───

const CONNECT_BRIEF: ActionBrief = {
  headline: 'Connect your Microsoft 365 tenant.',
  lede: 'A Global Administrator runs **one setup script**, about ten minutes. Apart from turning on Exchange organization customization if it’s off, and removing the Compliance Administrator role from Microsoft’s M365 Tenant Configuration Manager app unless your setup includes Purview compliance monitoring, it doesn’t change your policies or settings.',
}

const ALL_SET_BRIEF: ActionBrief = {
  headline: 'Nothing left to set up.',
  lede: '**Your monitoring settings** live under Baseline Compliance, and your dashboard is ready.',
  primaryAction: { label: 'Open your dashboard', href: '/dashboard' },
}

const WATCHING_BRIEF: ActionBrief = {
  headline: 'Nothing more for you to do.',
  lede: '**Continuous watch is on**, and your dashboard fills in as the first assessments land.',
  primaryAction: { label: 'Open your dashboard', href: '/dashboard' },
}

const RETRY_BRIEF: ActionBrief = {
  headline: 'Retry loading your setup.',
  lede: '**Nothing is lost**, and your setup picks up where it left off.',
}

export default function SetupWizard() {
  const queryClient = useQueryClient()

  const tenantsQuery = useQuery({
    queryKey: ['tenants'],
    queryFn: api.getTenants,
  })

  const tenant = pickSetupTenant(tenantsQuery.data)
  const tenantId = tenant?.tenantId

  const coverageQuery = useQuery({
    queryKey: ['tcm-coverage', tenantId],
    queryFn: async () => {
      const summary = await tcmApi.getCoverage(tenantId!)
      // Promotion flips Tenants.baselineSource server-side, but the setup
      // gate (Layout banner + auto-route) derives eligibility from the
      // ['tenants'] cache, which is fresh for 5 minutes — without this the
      // "Setup isn't finished" reminder outlives the finished setup (codex
      // P2). Same in-queryFn invalidation pattern as the sizing poller;
      // fires only on the actual non-Hybrid → Hybrid transition.
      if (
        tenantsCacheNeedsRefresh(
          summary.baselineSource,
          queryClient.getQueryData<Tenant[]>(['tenants']),
          tenantId!,
        )
      ) {
        void queryClient.invalidateQueries({ queryKey: ['tenants'] })
      }
      return summary
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    // Same bounded self-serve poll as the Monitoring tab: while the server is
    // auto-finishing setup this page advances on its own.
    refetchInterval: query =>
      coverageRefetchInterval(
        query.state.data as TcmCoverageSummary | undefined,
        query.state.fetchFailureCount,
      ),
  })

  const view = composeMonitoringCoverage({
    coverage: coverageQuery.data,
    isLoading: coverageQuery.isLoading,
    isError: coverageQuery.isError,
    hasTenant: !!tenantId,
  })
  const coverage = coverageQuery.data
  const pollStalled = coverageQuery.failureCount >= COVERAGE_POLL_MAX_FAILURES

  // ── The brief and the body per state ──────────────────────────────
  let brief: ActionBrief = CONNECT_BRIEF
  let briefLoading = false
  let done = false
  let body: React.ReactNode = null
  if (tenantsQuery.isLoading) {
    briefLoading = true
    body = <QuietNote>Getting your setup ready.</QuietNote>
  } else if (tenantsQuery.isError) {
    // A failed tenants fetch must NOT fall through to the "all set" brief —
    // a freshly-approved customer reaches /setup straight from the approval
    // email (rendered outside Layout, so no cache pre-warm), and a transient
    // 5xx / token-refresh blip on that first fetch would otherwise tell them
    // the opposite of the truth. Loud + retryable (silent-failure review F1).
    brief = RETRY_BRIEF
    body = (
      <LoadError title="We couldn’t load your account." onRetry={() => void tenantsQuery.refetch()} />
    )
  } else if (!tenant) {
    // Not eligible: already promoted, several tenants, or an operator
    // wandering in. Friendly all-set brief, never an error.
    brief = ALL_SET_BRIEF
    done = true
  } else if (view.state === 'loading') {
    briefLoading = true
    body = <QuietNote>Reading your setup progress.</QuietNote>
  } else if (view.state === 'error') {
    brief = RETRY_BRIEF
    body = <LoadError title="We could not load your setup progress." onRetry={() => void coverageQuery.refetch()} />
  } else if (view.journey && coverage && tenantId) {
    body = (
      <section aria-label="Setup journey" className="mt-12">
        <SectionHeadline level="page">{view.journey.headline}</SectionHeadline>
        <p className="m-0 mt-2 max-w-[56ch] text-body text-text-secondary">{view.journey.sub}</p>
        {view.journey.caveat && (
          <InlineNotification kind="info" title={view.journey.caveat} className="mt-4 max-w-2xl" />
        )}
        <SetupJourney
          key={tenantId}
          tenantId={tenantId}
          coverage={coverage}
          journey={view.journey}
          pollStalled={pollStalled}
          onCheckAgain={() => void coverageQuery.refetch()}
          relay
        />
      </section>
    )
  } else {
    // Journey composed to null with a tenant present = continuous watch is
    // on. The deferred promote finishes on its own — celebrate and hand off.
    brief = WATCHING_BRIEF
    done = true
    body = (
      <p className="m-0 mt-6 max-w-[56ch] text-body text-text-secondary">
        Guardian finishes the last of the wiring on its own.
      </p>
    )
  }

  return (
    <div className="flex min-h-screen flex-col">
      <BrandBar to="/dashboard" />

      <main id="main" className="min-w-0 flex-1">
        <div className="mx-auto max-w-[1056px] px-8 pb-16 pt-6 max-c-md:px-4 max-c-md:pb-12 max-c-md:pt-4">
          <BriefHero
            brief={brief}
            size="pillar"
            isLoading={briefLoading}
            className="mt-8"
            provenance={!done && tenant ? `Guardian setup · ${tenant.name}` : undefined}
          />

          {body}

          {/* Soft-gate escape + support fall-through. */}
          <div className="mt-12 flex flex-wrap items-center justify-between gap-x-6 gap-y-3 border-t border-line-subtle pt-6 text-body text-text-secondary">
            {done ? <span /> : (
              <Link to="/dashboard" className={PUBLIC_LINK}>
                Skip for now, look around first
              </Link>
            )}
            <p className="m-0 text-helper text-text-helper">
              Stuck?{' '}
              <a href="mailto:support@iuvotech.com" className={PUBLIC_LINK}>
                support@iuvotech.com
              </a>
            </p>
          </div>
        </div>
      </main>
    </div>
  )
}

/** While a query loads: a live status line under the brief's announced empty heading. */
function QuietNote({ children }: { children: React.ReactNode }) {
  return (
    <p role="status" className="m-0 mt-8 flex items-center gap-3 text-body text-text-secondary">
      <Spinner size={16} />
      {children}
    </p>
  )
}

/** A failed load: the blocking error with its retry. Nothing is lost, so the copy says so. */
function LoadError({ title, onRetry }: { title: string; onRetry: () => void }) {
  return (
    <InlineNotification
      kind="error"
      title={title}
      className="mt-8 max-w-2xl"
      actions={
        <Button kind="tertiary" size="sm" onClick={onRetry}>
          Retry
        </Button>
      }
    >
      Try again in a moment.
    </InlineNotification>
  )
}
